import {
  CapabilityNav,
  PageHeader,
  Table,
} from "@velocity-ehs/component-library";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Column } from "primereact/column";
import { ChangeEvent, useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useGetEhsRoles } from "../../../hooks/useGetEhsRoles";
import { SortDirection } from "../../supportFunctions/graphql/Queries";
import { navItems } from "../RoleManagement/NavHelpers";
import { useHistory } from "react-router-dom";
import * as Permissions from "../../supportFunctions/Permissions";
import UsersService from "@velocity-ehs/user-utils-module";

const defaultParams = {
  first: 0,
  rows: 20,
  page: 0,
  sortField: "name",
  sortOrder: SortDirection.ASC,
  filters: {
    name: {
      value: null,
    },
  },
};

type EhsRoleManagementProps = {
  addPermission: Array<string>;
};

const EhsRoleManagement = ({ addPermission }: EhsRoleManagementProps) => {
  const allFlags = useFlags();
  const history = useHistory();
  const { t } = useTranslation();

  const [lazyParams, setLazyParams] = useState(defaultParams);
  const { ehsRoles, isLoading, pagination } = useGetEhsRoles(lazyParams);

  const [totalRecords, setTotalRecords] = useState<number>(0);
  useEffect(() => {
    pagination.totalElements && setTotalRecords(pagination.totalElements);
  }, [pagination.totalElements]);
  const canAdd = (): boolean => {
    return Permissions.IsAllowed(addPermission, UsersService.permissions);
  };

  const onRowSelect = (event: any): void => {
    history.push(`/role-management/ehs-roles/${event.data.id}/edit`);
  };

  const handleGlobalSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setLazyParams((previous) => ({
        ...previous,
        filters: {
          ...previous.filters,
          name: {
            value: event.target.value,
          },
        },
      })),
    []
  );

  return (
    <div className="App">
      <CapabilityNav
        capability="Roles Management"
        navItems={navItems()}
        featureFlags={allFlags}
      />
      <div className="mainContent">
        <PageHeader
          title={t("universal_ehs_roles")}
          navigationTree={[
            { text: `Accelerate ${t("universal_Home")}`, href: "/" },
            { text: `EHS ${t("universal_Roles")}`, href: "" },
          ]}
          primaryButton={{
            label: t("universal_Add"),
            id: "addEhsRole",
            onClick: () => history.push("/role-management/ehs-roles/add"),
            visible: canAdd(),
          }}
        />
        <div className="card__fullContent">
          <Table
            data={ehsRoles}
            params={lazyParams}
            totalRecords={totalRecords}
            onRowSelect={onRowSelect}
            selectionMode="single"
            onPage={setLazyParams}
            onSort={setLazyParams}
            isLoading={isLoading}
            dataKey="code"
            onGlobalSearch={handleGlobalSearch}
            testId="ehs-role-management-table"
          >
            <Column field="name" header={t("universal_Role")} sortable />
            <Column field="description" header={t("universal_Description")} />
          </Table>
        </div>
      </div>
    </div>
  );
};

export default EhsRoleManagement;
